import { memo, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { FileIcon } from "@/components/common/FileIcon";
import { HighlightedText } from "@/components/common/HighlightedText";
import { CaseSensitiveToggle } from "@/components/common/CaseSensitiveToggle";
import { DisclosureChevron } from "@/components/common/Disclosure";
import { IconButton } from "@/components/common/IconButton";
import { SearchInput } from "@/components/common/SearchInput";
import { ShortcutHint } from "@/components/common/ShortcutHint";
import type { WorkspaceSearchSpanDto } from "@/lib/api";
import { splitPath } from "@/lib/environment-files";
import { getFileIconType } from "@/lib/file-type";
import { SECTION_HEADER_STYLE, STICKY_HEADER_STYLE } from "@/lib/pane-styles";
import { ROW_HOVER_BG, ROW_SELECTED_BG } from "@/lib/row-styles";
import { FOCUS_WORKSPACE_SEARCH_EVENT } from "@/lib/shortcut-defs";
import {
	closeWorkspaceSearch,
	invalidateSearch,
	MIN_AUTO_SEARCH_QUERY_LENGTH,
	resolveFileIdByPath,
	runWorkspaceSearch,
	SEARCH_DEBOUNCE_MS,
	spansToHighlightRuns,
} from "@/lib/workspace-search";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useSearchStore } from "@/stores/useSearchStore";

/** カウントバッジ。円形にするため最小幅と行の高さを同じ値から作る。 */
const COUNT_BADGE_SIZE = 16;

export function WorkspaceSearchPane() {
	const inputRef = useRef<HTMLInputElement>(null);
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const query = useSearchStore((state) => state.query);
	const caseSensitive = useSearchStore((state) => state.caseSensitive);
	const setQuery = useSearchStore((state) => state.setQuery);
	const toggleCaseSensitive = useSearchStore((state) => state.toggleCaseSensitive);
	const closeSearch = closeWorkspaceSearch;

	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const focusInput = () => {
		inputRef.current?.focus();
		inputRef.current?.select();
	};

	useEffect(() => {
		focusInput();
		window.addEventListener(FOCUS_WORKSPACE_SEARCH_EVENT, focusInput);
		return () => window.removeEventListener(FOCUS_WORKSPACE_SEARCH_EVENT, focusInput);
	}, []);

	// 入力停止から SEARCH_DEBOUNCE_MS 後に自動検索する。MIN_AUTO_SEARCH_QUERY_LENGTH 未満は
	// 都度の exec コスト (K8s 経由でサンドボックスへ rg を実行する) が見合わないため、
	// 明示的な Enter キー実行のみ受け付ける。
	useEffect(() => {
		if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		if (query.trim().length < MIN_AUTO_SEARCH_QUERY_LENGTH || !environmentId) {
			invalidateSearch();
			return;
		}
		debounceTimerRef.current = setTimeout(() => {
			void runWorkspaceSearch(environmentId);
		}, SEARCH_DEBOUNCE_MS);
		return () => {
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		};
	}, [query, caseSensitive, environmentId]);

	const submit = () => {
		if (!environmentId) return;
		if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		void runWorkspaceSearch(environmentId);
	};

	const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
		if (event.nativeEvent.isComposing) return;
		if (event.key === "Enter") {
			event.preventDefault();
			submit();
		}
		if (event.key === "Escape") {
			event.preventDefault();
			closeSearch();
		}
	};

	return (
		<div data-testid="workspace-search-pane" style={{ minHeight: "100%" }}>
			{/* 結果を辿っている間も再検索・閉じる操作に届くよう、見出しと入力欄をまとめて固定する。 */}
			<div style={STICKY_HEADER_STYLE}>
				<div style={SECTION_HEADER_STYLE}>
					<span>検索</span>
					<IconButton
						icon="x"
						onClick={closeSearch}
						title="検索を閉じる"
						data-testid="workspace-search-close"
					/>
				</div>
				<div style={{ padding: "0 12px 8px" }}>
					<SearchInput
						ref={inputRef}
						value={query}
						onChange={setQuery}
						onKeyDown={handleKeyDown}
						placeholder="ワークスペースを検索"
						testId="workspace-search-input"
						trailing={
							<>
								<CaseSensitiveToggle
									active={caseSensitive}
									onToggle={toggleCaseSensitive}
									testId="workspace-search-case-sensitive"
								/>
								<ShortcutHint combo="⇧⌘F" />
							</>
						}
					/>
				</div>
			</div>
			<WorkspaceSearchResults />
		</div>
	);
}

/**
 * 検索結果の表示のみを担当する。`query` を購読しないため、入力中のキーストロークで
 * 再レンダーされない (親の WorkspaceSearchPane から独立して検索結果の変化にのみ反応する)。
 */
const WorkspaceSearchResults = memo(function WorkspaceSearchResults() {
	const status = useSearchStore((state) => state.status);
	const files = useSearchStore((state) => state.files);
	const matchCount = useSearchStore((state) => state.matchCount);
	const truncated = useSearchStore((state) => state.truncated);
	const error = useSearchStore((state) => state.error);
	// ファイルツリー (FileTreeNode) と同じホバー/選択の見た目にする。
	const resultsVersion = useSearchStore((state) => state.resultsVersion);
	const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() => new Set());
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);
	const [selectedMatchKey, setSelectedMatchKey] = useState<string | null>(null);

	// 新しい検索結果が来たとき (resultsVersion) だけリセットする。ファイルエディタでの編集を
	// ライブ反映する updateFileMatches は resultsVersion を進めないため、選択中に他ファイルを
	// 編集しても選択状態が消えない。
	useEffect(() => {
		setCollapsedPaths(new Set());
		setSelectedMatchKey(null);
	}, [resultsVersion]);

	const openMatch = async (
		path: string,
		lineNumber: number,
		spans: readonly WorkspaceSearchSpanDto[],
	) => {
		// 解決を待っている間にユーザーが別のチャットへ切り替えていたら、切替後のチャットへ
		// このクリックのファイルオープン・行ジャンプを書き込まないようにする。
		const chatId = useChatStore.getState().chatId;
		// query を購読すると入力中のキーストロークで再レンダーされてしまうため (このコンポーネント
		// の memo 化の意図)、クリック時に一度だけ getState() で読む。
		const { query, caseSensitive } = useSearchStore.getState();
		const fileId = await resolveFileIdByPath(path);
		if (!fileId || useChatStore.getState().chatId !== chatId) return;
		const chat = useChatStore.getState();
		chat.selectEnvironmentFilesTreeNode(path);
		chat.openFileAtLine(fileId, lineNumber, spans, query, caseSensitive);
	};

	const resultSummary =
		status === "loading"
			? "検索中…"
			: status === "done" && matchCount > 0
				? `${matchCount}件（${files.length}ファイル）`
				: null;

	return (
		<>
			{resultSummary && (
				<p style={{ margin: "0 12px 6px", fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					{resultSummary}
				</p>
			)}
			{status === "error" && (
				<p
					data-testid="workspace-search-error"
					style={{ margin: "0 12px 8px", fontSize: "var(--text-caption)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{status === "done" && files.length === 0 && (
				<p
					data-testid="workspace-search-empty"
					style={{ margin: "0 12px 8px", fontSize: "var(--text-caption)", color: "var(--fg-3)" }}
				>
					一致する結果がありません
				</p>
			)}
			{truncated && (
				<p
					data-testid="workspace-search-truncated"
					style={{ margin: "0 12px 8px", fontSize: "var(--text-caption)", color: "var(--fg-3)" }}
				>
					結果が多いため500件で打ち切りました
				</p>
			)}
			{files.map((file) => {
				const { dir, name } = splitPath(file.path);
				const collapsed = collapsedPaths.has(file.path);
				return (
					<div key={file.path} data-testid={`workspace-search-file-${file.path}`}>
						<button
							type="button"
							className="text-list"
							onClick={() =>
								setCollapsedPaths((paths) => {
									const next = new Set(paths);
									if (next.has(file.path)) next.delete(file.path);
									else next.add(file.path);
									return next;
								})
							}
							onMouseEnter={() => setHoveredKey(file.path)}
							onMouseLeave={() => setHoveredKey((key) => (key === file.path ? null : key))}
							style={{
								display: "flex",
								width: "100%",
								alignItems: "center",
								gap: 6,
								padding: "5px 12px",
								border: 0,
								background: hoveredKey === file.path ? ROW_HOVER_BG : "transparent",
								color: "var(--fg-1)",
								cursor: "pointer",
								textAlign: "left",
							}}
						>
							<DisclosureChevron open={!collapsed} size={11} />
							<FileIcon type={getFileIconType(name)} />
							<span
								style={{
									minWidth: 0,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									fontWeight: 500,
								}}
							>
								{name}
							</span>
							{dir && (
								<span
									style={{
										minWidth: 0,
										flex: 1,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										color: "var(--fg-3)",
										fontSize: "var(--text-caption)",
									}}
								>
									{dir}
								</span>
							)}
							<span
								style={{
									marginLeft: "auto",
									minWidth: COUNT_BADGE_SIZE,
									padding: "0 6px",
									borderRadius: "var(--radius-full)",
									background: "var(--accent-subtle)",
									color: "var(--accent)",
									fontFamily: "var(--font-mono)",
									fontSize: "var(--text-section)",
									fontWeight: 500,
									lineHeight: `${COUNT_BADGE_SIZE}px`,
									textAlign: "center",
								}}
							>
								{file.matches.length}
							</span>
						</button>
						{!collapsed &&
							file.matches.map((match) => {
								const matchKey = `${file.path}:${match.lineNumber}:${match.lineText}`;
								const isSelected = selectedMatchKey === matchKey;
								const isHovered = hoveredKey === matchKey;
								return (
									<button
										key={matchKey}
										type="button"
										className="text-list"
										onClick={() => {
											setSelectedMatchKey(matchKey);
											void openMatch(file.path, match.lineNumber, match.spans);
										}}
										onMouseEnter={() => setHoveredKey(matchKey)}
										onMouseLeave={() => setHoveredKey((key) => (key === matchKey ? null : key))}
										data-testid={`workspace-search-match-${file.path}-${match.lineNumber}`}
										style={{
											display: "flex",
											width: "100%",
											padding: "3px 12px 3px 20px",
											border: 0,
											background: isSelected
												? ROW_SELECTED_BG
												: isHovered
													? ROW_HOVER_BG
													: "transparent",
											color: "var(--fg-2)",
											fontFamily: "var(--font-mono)",
											cursor: "pointer",
											textAlign: "left",
										}}
									>
										<span
											style={{
												minWidth: 0,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "pre",
											}}
										>
											<HighlightedText
												runs={spansToHighlightRuns(match.lineText, match.spans)}
												highlightStyle="background"
											/>
										</span>
									</button>
								);
							})}
					</div>
				);
			})}
		</>
	);
});
